// What happened to one envelope. spec/04 §2, §7.
//
// The question a sender actually has is "who still has to sign", and until the
// read returned parties there was no way to answer it. This is that answer,
// plus the two things people ask next: give me the document, and show me what
// the record says.
//
// The audit trail is shown with its verification rather than as a list of
// lines. A hash chain nobody checks is decoration, and the server checks this
// window on every read (spec/04 §7) — so the result of that check belongs on
// screen, including when it fails.

import { useCallback, useEffect, useState, type ReactElement } from 'react';
import {
  ApiError,
  downloadItem,
  readAudit,
  readEnvelope,
  resendLink,
  type AuditPage,
  type EnvelopeDetail,
  type ResendResult,
} from './api.ts';
import { readSession } from './session.ts';

function messageOf(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return 'Something between this page and the API answered instead.';
}

/** Plain words for a status, because PENDING is jargon to everybody else. */
const PARTY_STATUS: Record<string, string> = {
  PENDING: 'Not opened yet',
  OPENED: 'Opened, not signed',
  COMPLETED: 'Signed',
  DECLINED: 'Declined',
  EXPIRED: 'Expired',
};

/**
 * Whether a fresh link is worth issuing to a party in this status.
 *
 * The API refuses the rest (spec/04 §3); this only keeps the page from
 * offering a button whose sole outcome is an error. An expired party is
 * excluded here too — their link cannot be made to work by reissuing it.
 */
function canResend(status: string): boolean {
  return status === 'PENDING' || status === 'OPENED';
}

export function EnvelopePage({ id }: { id: string }): ReactElement {
  const session = readSession();
  const token = session?.token ?? '';

  const [envelope, setEnvelope] = useState<EnvelopeDetail | null>(null);
  const [audit, setAudit] = useState<AuditPage | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [auditFailure, setAuditFailure] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [resending, setResending] = useState<string | null>(null);
  /**
   * What the last resend produced, by party id.
   *
   * Kept on screen rather than flashed: for a sender-distributed envelope this
   * is the only place the new link exists — it is never stored — and for an
   * emailed one the sender still needs to be told the previous link is dead
   * (spec/04 §3).
   */
  const [resent, setResent] = useState<Record<string, ResendResult>>({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setEnvelope(await readEnvelope(token, id));
    // Separately, and allowed to fail on its own: a trail that cannot be read
    // must not hide the envelope, and reading the trail needs a scope that
    // reading the envelope does not.
    try {
      setAudit(await readAudit(token, id));
    } catch (error) {
      setAuditFailure(messageOf(error));
    }
  }, [token, id]);

  useEffect(() => {
    if (session === null) {
      window.location.assign('/login');
      return;
    }
    load()
      .catch((error: unknown) => setFailure(messageOf(error)))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  if (session === null) return <main className="wrap" />;

  async function download(itemId: string, filename: string): Promise<void> {
    setDownloading(itemId);
    try {
      const blob = await downloadItem(token, id, itemId);
      // An ordinary link to an object URL, revoked once the browser has taken
      // it. NN-07: nothing here may inhibit downloading, printing or keeping a
      // copy, so this is the plainest mechanism there is.
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      setFailure(messageOf(error));
    } finally {
      setDownloading(null);
    }
  }

  async function resend(partyId: string): Promise<void> {
    setResending(partyId);
    setFailure(null);
    try {
      const result = await resendLink(token, id, partyId);
      setResent((current) => ({ ...current, [partyId]: result }));
      // The party's status can move with the re-issue, so the row is re-read
      // rather than patched from whatever this response happened to carry.
      setEnvelope(await readEnvelope(token, id));
    } catch (error) {
      setFailure(messageOf(error));
    } finally {
      setResending(null);
    }
  }

  if (loading) {
    return (
      <main className="wrap">
        <h1>Envelope</h1>
        <p className="muted">Loading…</p>
      </main>
    );
  }

  if (envelope === null) {
    return (
      <main className="wrap">
        <h1>Envelope</h1>
        <div className="error" role="alert">
          <p>{failure ?? 'That envelope could not be loaded.'}</p>
        </div>
        <p className="muted">
          <a href="/envelopes">Back to envelopes</a>
        </p>
      </main>
    );
  }

  const signers = envelope.parties.filter((party) => party.role !== 'CC');

  return (
    <main className="wrap">
      <h1>{envelope.title}</h1>
      <p className="muted">
        <a href="/envelopes">Envelopes</a> · {envelope.status} · created{' '}
        {new Date(envelope.createdAt).toLocaleString()}
        {envelope.completedAt === null
          ? ''
          : ` · completed ${new Date(envelope.completedAt).toLocaleString()}`}
      </p>

      {failure !== null && (
        <div className="error" role="alert">
          <p>{failure}</p>
        </div>
      )}

      <section className="setting" aria-labelledby="parties-heading">
        <h2 id="parties-heading">Signers</h2>
        <table className="envelopes">
          <caption className="muted">Who was asked, in signing order, and where each stands.</caption>
          <thead>
            <tr>
              <th scope="col">#</th>
              <th scope="col">Name</th>
              <th scope="col">Email</th>
              <th scope="col">Role</th>
              <th scope="col">Status</th>
              <th scope="col">Link</th>
            </tr>
          </thead>
          <tbody>
            {signers.map((party) => (
              <tr key={party.id}>
                <td>{party.signingOrder}</td>
                <td>{party.name ?? '—'}</td>
                <td>{party.email ?? '—'}</td>
                <td>
                  {party.role}
                  {party.hasAccessCode && ' · access code'}
                </td>
                <td>{PARTY_STATUS[party.status] ?? party.status}</td>
                <td>
                  {canResend(party.status) && envelope.status === 'PENDING' ? (
                    <button
                      type="button"
                      onClick={() => void resend(party.id)}
                      disabled={resending !== null}
                    >
                      {resending === party.id ? 'Issuing…' : 'Send a new link'}
                    </button>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {Object.entries(resent).map(([partyId, result]) => (
          <div className="notice" role="status" key={partyId}>
            {result.delivery === 'QUEUED' ? (
              <p>
                A new link is queued for sending. The previous one has stopped
                working — reissuing always makes a new link, because nothing
                here can reproduce the old one.
              </p>
            ) : (
              <>
                <p>
                  A new link, and the previous one has stopped working. It is
                  not stored anywhere, so copy it now or issue another.
                </p>
                {/* Selectable text rather than a mailto: or a copy button that
                    silently fails. The sender is the one delivering it. */}
                <p className="hash">{result.url}</p>
              </>
            )}
          </div>
        ))}
      </section>

      <section className="setting" aria-labelledby="documents-heading">
        <h2 id="documents-heading">Documents</h2>
        <ul className="documents">
          {envelope.items.map((item) => (
            <li key={item.id}>
              <span>
                {item.filename}
                {item.pageCount === null ? '' : ` · ${item.pageCount} page${item.pageCount === 1 ? '' : 's'}`}
              </span>{' '}
              <button
                type="button"
                onClick={() => void download(item.id, item.filename)}
                disabled={downloading !== null}
              >
                {downloading === item.id ? 'Downloading…' : 'Download'}
              </button>
            </li>
          ))}
        </ul>
      </section>

      <section className="setting" aria-labelledby="audit-heading">
        <h2 id="audit-heading">Audit trail</h2>

        {auditFailure !== null ? (
          <div className="error" role="alert">
            <p>{auditFailure}</p>
          </div>
        ) : audit === null ? (
          <p className="muted">No trail was returned.</p>
        ) : (
          <>
            {/*
              The verification result, stated. The server checks the record
              hashes and the sequence contiguity of this window on every read;
              showing the records without saying whether that held would be
              presenting a hash chain as decoration.
            */}
            <div className={audit.verification.ok ? 'notice' : 'error'} role="status">
              <p>
                {audit.verification.ok
                  ? `Verified: ${audit.verification.checked.join(' and ').toLowerCase().replace(/_/g, ' ')} hold across records ${audit.verification.from ?? '—'}–${audit.verification.to ?? '—'}.`
                  : `This window did not verify${audit.verification.failedAtSeq === undefined ? '' : ` at record ${audit.verification.failedAtSeq}`}: ${audit.verification.reason ?? 'no reason given'}.`}
              </p>
            </div>

            <table className="envelopes">
              <caption className="muted">
                Every recorded event, oldest first. Append-only at the database
                layer (NN-03).
              </caption>
              <thead>
                <tr>
                  <th scope="col">#</th>
                  <th scope="col">Event</th>
                  <th scope="col">When</th>
                </tr>
              </thead>
              <tbody>
                {audit.data.map((record) => (
                  <tr key={record.eventId}>
                    <td>{record.seq}</td>
                    <td>{record.type}</td>
                    <td>{new Date(record.occurredAt).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {audit.nextCursor !== null && (
              <p className="muted">
                Showing the first 100 records; this trail has more.
              </p>
            )}
          </>
        )}
      </section>
    </main>
  );
}
